// Every request to our API, before anything cabinet-specific: it waits for the WAF token, sends the cookie across
// origins, and goes out in the language the person chose. The Blazor app's WafTokenCredentialsHandler.

import { log } from './logs.ts';
import { redact } from './redact.ts';
import { state } from './state.ts';
import { goesToOurApi, wafReady } from './waf.ts';

export class ApiResponse {
    /** 0 when the request never got an answer. */
    readonly status: number;
    readonly text: string;

    constructor(status: number, text: string) {
        this.status = status;
        this.text = text;
    }

    get ok(): boolean {
        return this.status >= 200 && this.status < 300;
    }

    /** The body as JSON, or null when it is not. What it holds is the caller's to say. */
    json<T = unknown>(): T | null {
        try {
            return JSON.parse(this.text) as T;
        } catch {
            return null;
        }
    }

    /** The body as a plain string: a JSON string loses its quotes, anything else is the text as it came. */
    string(): string {
        const value = this.json();

        return typeof value === 'string' ? value : this.text;
    }
}

export function join(base: string, path: string): string {
    return base.replace(/\/$/, '') + '/' + path.replace(/^\//, '');
}

export interface SendOptions {
    method?: string;
    json?: unknown;
    formUrlEncoded?: Record<string, string | null | undefined>;
    headers?: Record<string, string>;
    bearer?: string | null;
    language?: string | null;
    body?: string;
}

export async function send(url: string, options: SendOptions = {}): Promise<ApiResponse> {
    const { method = 'GET', json, formUrlEncoded, bearer, language, body: rawBody } = options;
    const ours = goesToOurApi(url);

    // Before the credentials, not after: including them is pointless if the cookie they carry is not written yet.
    if (ours) await wafReady();

    const headers = new Headers(options.headers || {});
    const acceptLanguage = language === undefined ? state.locale : language;

    if (acceptLanguage && !headers.has('Accept-Language')) headers.set('Accept-Language', acceptLanguage);
    if (bearer) headers.set('Authorization', 'Bearer ' + bearer);

    let body = rawBody;

    if (json !== undefined) {
        body = JSON.stringify(json);
        headers.set('Content-Type', 'application/json; charset=utf-8');
    } else if (formUrlEncoded) {
        const form = new URLSearchParams();

        for (const [key, value] of Object.entries(formUrlEncoded)) {
            if (value !== null && value !== undefined) form.append(key, value);
        }

        body = form.toString();
        headers.set('Content-Type', 'application/x-www-form-urlencoded');
    } else if (body !== undefined && !headers.has('Content-Type')) {
        headers.set('Content-Type', 'text/plain; charset=utf-8');
    }

    try {
        const response = await fetch(url, { method, headers, body, credentials: ours ? 'include' : 'same-origin' });

        return new ApiResponse(response.status, await response.text());
    } catch (error) {
        log.warn(`HTTP ${method} ${redact(url)} failed: ${error instanceof Error ? error.message : String(error)}`, undefined, 'Lify.Influencer.Http');

        return new ApiResponse(0, '');
    }
}
